import { createTheme, responsiveFontSizes } from '@mui/material/styles';

const colors = {
  primary: {
    main: '#1E3A5F',
    light: '#2E5A8F',
    dark: '#0E2A4F',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#C9A961',
    light: '#D9C991',
    dark: '#A98941',
    contrastText: '#1E3A5F',
  },
  background: {
    default: '#F8F9FC',
    paper: '#FFFFFF',
    dark: '#1E3A5F',
  },
  accent: {
    main: '#2D9CDB',
    light: '#5DB8EB',
    dark: '#1D7CBB',
  },
  text: {
    primary: '#1E3A5F',
    secondary: '#5A6B7D',
    disabled: '#9CA3AF',
  },
  grey: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },
};

let theme = createTheme({
  palette: {
    mode: 'light',
    primary: colors.primary,
    secondary: colors.secondary,
    background: colors.background,
    text: colors.text,
    grey: colors.grey,
    accent: colors.accent,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Playfair Display", Georgia, serif',
      fontWeight: 700,
      fontSize: '3.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: '"Playfair Display", Georgia, serif',
      fontWeight: 700,
      fontSize: '2.75rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: '"Playfair Display", Georgia, serif',
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.4,
    },
    h4: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.5,
    },
    h6: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    subtitle1: {
      fontFamily: '"Inter", sans-serif',
      fontSize: '1.125rem',
      lineHeight: 1.6,
      fontWeight: 500,
    },
    subtitle2: {
      fontFamily: '"Inter", sans-serif',
      fontSize: '0.875rem',
      lineHeight: 1.6,
      fontWeight: 500,
    },
    body1: {
      fontFamily: '"Inter", sans-serif',
      fontSize: '1rem',
      lineHeight: 1.7,
    },
    body2: {
      fontFamily: '"Inter", sans-serif',
      fontSize: '0.875rem',
      lineHeight: 1.7,
    },
    button: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      fontSize: '0.875rem',
      textTransform: 'none',
      letterSpacing: '0.02em',
    },
    caption: {
      fontFamily: '"Inter", sans-serif',
      fontSize: '0.75rem',
      lineHeight: 1.5,
    },
    overline: {
      fontFamily: '"Inter", sans-serif',
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(30, 58, 95, 0.05)',
    '0px 4px 8px rgba(30, 58, 95, 0.08)',
    '0px 6px 12px rgba(30, 58, 95, 0.1)',
    '0px 8px 16px rgba(30, 58, 95, 0.12)',
    '0px 10px 20px rgba(30, 58, 95, 0.14)',
    '0px 12px 24px rgba(30, 58, 95, 0.16)',
    '0px 14px 28px rgba(30, 58, 95, 0.18)',
    '0px 16px 32px rgba(30, 58, 95, 0.2)',
    '0px 18px 36px rgba(30, 58, 95, 0.22)',
    '0px 20px 40px rgba(30, 58, 95, 0.24)',
    '0px 22px 44px rgba(30, 58, 95, 0.26)',
    '0px 24px 48px rgba(30, 58, 95, 0.28)',
    '0px 26px 52px rgba(30, 58, 95, 0.3)',
    '0px 28px 56px rgba(30, 58, 95, 0.32)',
    '0px 30px 60px rgba(30, 58, 95, 0.34)',
    '0px 32px 64px rgba(30, 58, 95, 0.36)',
    '0px 34px 68px rgba(30, 58, 95, 0.38)',
    '0px 36px 72px rgba(30, 58, 95, 0.4)',
    '0px 38px 76px rgba(30, 58, 95, 0.42)',
    '0px 40px 80px rgba(30, 58, 95, 0.44)',
    '0px 42px 84px rgba(30, 58, 95, 0.46)',
    '0px 44px 88px rgba(30, 58, 95, 0.48)',
    '0px 46px 92px rgba(30, 58, 95, 0.5)',
    '0px 48px 96px rgba(30, 58, 95, 0.52)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        html {
          scroll-behavior: smooth;
        }

        body {
          background-color: #F8F9FC;
        }

        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: #F3F4F6;
        }

        ::-webkit-scrollbar-thumb {
          background: #C9A961;
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #A98941;
        }
      `,
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '0.9rem',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(30, 58, 95, 0.15)',
          },
        },
        contained: {
          '&:hover': {
            transform: 'translateY(-1px)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #1E3A5F 0%, #2E5A8F 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #2E5A8F 0%, #1E3A5F 100%)',
          },
        },
        containedSecondary: {
          color: '#1E3A5F',
          '&:hover': {
            backgroundColor: '#D9C991',
          },
        },
        outlinedPrimary: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            backgroundColor: 'rgba(30, 58, 95, 0.04)',
          },
        },
        outlinedSecondary: {
          borderWidth: 2,
          borderColor: '#C9A961',
          color: '#C9A961',
          '&:hover': {
            borderWidth: 2,
            borderColor: '#A98941',
            backgroundColor: 'rgba(201, 169, 97, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 4px 20px rgba(30, 58, 95, 0.08)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0px 12px 32px rgba(30, 58, 95, 0.12)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#C9A961',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#1E3A5F',
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1E3A5F',
          color: '#FFFFFF',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
        filled: {
          backgroundColor: 'rgba(201, 169, 97, 0.1)',
          color: '#C9A961',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: '#C9A961',
          color: '#1E3A5F',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(30, 58, 95, 0.1)',
        },
      },
    },
  },
});

theme = responsiveFontSizes(theme);

export { colors };
export default theme;
